import React, { Suspense, lazy, Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './store/AuthContext';
import { SyncProvider } from './store/SyncContext';
import AppShell from './components/layout/AppShell';
import LoadingSpinner from './components/ui/LoadingSpinner';

// Error Boundary for graceful error handling
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[VAHD] Uncaught error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: 40, textAlign: 'center', fontFamily: 'Inter, sans-serif',
          minHeight: '100vh', display: 'flex', flexDirection: 'column',
          justifyContent: 'center', alignItems: 'center',
          background: '#f9fafb'
        }}>
          <h2 style={{ color: '#dc2626', marginBottom: 8 }}>Something went wrong</h2>
          <p style={{ color: '#6b7280', marginBottom: 24 }}>
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 24px', background: '#059669', color: '#fff',
              border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}



const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const DailyProgressPage = lazy(() => import('./pages/dashboard/DailyProgressPage'));
const CasesPage = lazy(() => import('./pages/clinical/CasesPage'));
const VaccinationsPage = lazy(() => import('./pages/clinical/VaccinationsPage'));
const DewormingPage = lazy(() => import('./pages/clinical/DewormingPage'));
const AIServicePage = lazy(() => import('./pages/clinical/AIServicePage'));
const HRMSPage = lazy(() => import('./pages/hrms/HRMSPage'));
const EmployeesPage = lazy(() => import('./pages/hrms/EmployeesPage'));
const AttendancePage = lazy(() => import('./pages/hrms/AttendancePage'));
const AttendanceApprovePage = lazy(() => import('./pages/hrms/AttendanceApprovePage'));
const AttendanceReportPage = lazy(() => import('./pages/hrms/AttendanceReportPage'));
const LeavePage = lazy(() => import('./pages/hrms/LeavePage'));
const DrugsPage = lazy(() => import('./pages/drugs/DrugsPage'));
const StockPage = lazy(() => import('./pages/drugs/StockPage'));
const IndentsPage = lazy(() => import('./pages/drugs/IndentsPage'));
const AllocationsPage = lazy(() => import('./pages/drugs/AllocationsPage'));
const SalePage = lazy(() => import('./pages/drugs/SalePage'));
const FarmersPage = lazy(() => import('./pages/farmers/FarmersPage'));
const UploadFarmersPage = lazy(() => import('./pages/farmers/UploadFarmersPage'));
const FodderPage = lazy(() => import('./pages/fodder/FodderPage'));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'));
const ReportDetailPage = lazy(() => import('./pages/reports/ReportDetailPage'));
const DailyAttendanceReport = lazy(() => import('./pages/reports/DailyAttendanceReport'));
const DailyAttendanceAbstract = lazy(() => import('./pages/reports/DailyAttendanceAbstract'));
const RangeAttendanceReport = lazy(() => import('./pages/reports/RangeAttendanceReport'));
const LateAttendanceReport = lazy(() => import('./pages/reports/LateAttendanceReport'));
const NotLoggedOutReport = lazy(() => import('./pages/reports/NotLoggedOutReport'));
const NotLoggedOutAbstractReport = lazy(() => import('./pages/reports/NotLoggedOutAbstractReport'));
const LateAttendanceAbstractReport = lazy(() => import('./pages/reports/LateAttendanceAbstractReport'));
const EarlyLeavingAbstractReport = lazy(() => import('./pages/reports/EarlyLeavingAbstractReport'));
const CaseTreatedReport = lazy(() => import('./pages/reports/CaseTreatedReport'));
const AIServicesReport = lazy(() => import('./pages/reports/AIServicesReport'));
const FarmersReport = lazy(() => import('./pages/reports/FarmersReport'));
const FodderReport = lazy(() => import('./pages/reports/FodderReport'));
const DistrictAbstractReport = lazy(() => import('./pages/reports/DistrictAbstractReport'));
const AttendanceAbstractReport = lazy(() => import('./pages/reports/AttendanceAbstractReport'));
const DistrictWiseAbstractReport = lazy(() => import('./pages/reports/DistrictWiseAbstractReport'));
const DistrictWiseInstitutionCountReport = lazy(() => import('./pages/reports/DistrictWiseInstitutionCountReport'));
const InstitutionWiseDrugReport = lazy(() => import('./pages/reports/InstitutionWiseDrugReport'));
const InstitutionProgressReport = lazy(() => import('./pages/reports/InstitutionProgressReport'));
const DistrictWiseDetailedDrugReport = lazy(() => import('./pages/reports/DistrictWiseDetailedDrugReport'));
const DistrictWiseDrugReport = lazy(() => import('./pages/reports/DistrictWiseDrugReport'));
const GroupWiseDistrictIndentReport = lazy(() => import('./pages/reports/GroupWiseDistrictIndentReport'));

const DistrictWiseAbstractReport2627 = lazy(() => import('./pages/reports/DistrictWiseAbstractReport2627'));
const DistrictWiseInstitutionCountReport2627 = lazy(() => import('./pages/reports/DistrictWiseInstitutionCountReport2627'));
const InstitutionWiseDrugReport2627 = lazy(() => import('./pages/reports/InstitutionWiseDrugReport2627'));
const InstitutionProgressReport2627 = lazy(() => import('./pages/reports/InstitutionProgressReport2627'));
const DistrictWiseDetailedDrugReport2627 = lazy(() => import('./pages/reports/DistrictWiseDetailedDrugReport2627'));
const DistrictWiseDrugReport2627 = lazy(() => import('./pages/reports/DistrictWiseDrugReport2627'));
const GroupWiseDistrictIndentReport2627 = lazy(() => import('./pages/reports/GroupWiseDistrictIndentReport2627'));
const IoTPage = lazy(() => import('./pages/iot/IoTPage'));
const SyncPage = lazy(() => import('./pages/sync/SyncPage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));

const CitizenRegistrationPage = lazy(() => import('./pages/citizen/CitizenRegistrationPage'));
const CitizenFodderSeedPage = lazy(() => import('./pages/citizen/CitizenFodderSeedPage'));
const CitizenSexSortedSemenPage = lazy(() => import('./pages/citizen/CitizenSexSortedSemenPage'));
const CitizenDewormingPage = lazy(() => import('./pages/citizen/CitizenDewormingPage'));
const CitizenAIPage = lazy(() => import('./pages/citizen/CitizenAIPage'));
const CitizenGrievancesPage = lazy(() => import('./pages/citizen/CitizenGrievancesPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30000,
      refetchOnWindowFocus: false,
    },
    mutations: { retry: 0 },
  },
});

/**
 * permKey: the module key checked against user.permissions[permKey].view
 * If omitted, only authentication is required (no permission check).
 */
const protectedRoutes = [
  { path: 'dashboard',         Component: DashboardPage,        permKey: 'dashboard'           },
  { path: 'daily-progress',    Component: DailyProgressPage,    permKey: 'daily_progress'       },
  { path: 'cases',             Component: CasesPage,            permKey: 'clinical_cases'       },
  { path: 'vaccinations',      Component: VaccinationsPage,     permKey: 'clinical_vaccinations'},
  { path: 'deworming',         Component: DewormingPage,        permKey: 'clinical_deworming'   },
  { path: 'ai-service',        Component: AIServicePage,        permKey: 'clinical_ai_service'  },
  { path: 'hrms',              Component: HRMSPage,             permKey: 'hrms_overview'        },
  { path: 'employees',         Component: EmployeesPage,        permKey: 'hrms_employees'       },
  { path: 'attendance',        Component: AttendancePage,       permKey: 'hrms_attendance'      },
  { path: 'attendance-approve',Component: AttendanceApprovePage,permKey: 'hrms_attendance_approve'},
  { path: 'attendance-report', Component: AttendanceReportPage, permKey: 'hrms_attendance_report'},
  { path: 'leaves',            Component: LeavePage,            permKey: 'hrms_leave'           },
  { path: 'drugs',             Component: DrugsPage,            permKey: 'drugs_products'       },
  { path: 'stock',             Component: StockPage,            permKey: 'drugs_stock'          },
  { path: 'indents',           Component: IndentsPage,          permKey: 'drugs_indents'        },
  { path: 'allocations',       Component: AllocationsPage,      permKey: 'drugs_allocations'    },
  { path: 'drug-sale',         Component: SalePage,             permKey: 'drugs_sale'           },
  { path: 'farmers',           Component: FarmersPage,          permKey: 'hrms_farmers'         },
  { path: 'farmers/upload',    Component: UploadFarmersPage,    permKey: 'hrms_farmers'         },
  { path: 'fodder',            Component: FodderPage,           permKey: 'fodder'               },
  { path: 'iot',               Component: IoTPage,              permKey: 'iot'                  },
  { path: 'sync',              Component: SyncPage,             permKey: 'sync'                 },
  { path: 'reports',           Component: ReportsPage,          permKey: 'reports'              },
  { path: 'reports/daily-attendance', Component: DailyAttendanceReport, permKey: 'reports_daily_attendance' },
  { path: 'reports/daily-abstract', Component: DailyAttendanceAbstract, permKey: 'reports_daily_abstract' },
  { path: 'reports/attendance-abstract', Component: AttendanceAbstractReport, permKey: 'reports_attendance_abstract' },
  { path: 'reports/range-attendance', Component: RangeAttendanceReport, permKey: 'reports_range_attendance' },
  { path: 'reports/late-attendance', Component: LateAttendanceReport, permKey: 'reports_late_attendance' },
  { path: 'reports/not-logged-out', Component: NotLoggedOutReport, permKey: 'reports_not_logged_out' },
  { path: 'reports/not-logged-out-abstract', Component: NotLoggedOutAbstractReport, permKey: 'reports_not_logged_out_abstract' },
  { path: 'reports/late-attendance-abstract', Component: LateAttendanceAbstractReport, permKey: 'reports_late_attendance_abstract' },
  { path: 'reports/early-leaving-abstract', Component: EarlyLeavingAbstractReport, permKey: 'reports_early_leaving_abstract' },
  { path: 'reports/cases',     Component: CaseTreatedReport,    permKey: 'reports_cases' },
  { path: 'reports/ai',        Component: AIServicesReport,     permKey: 'reports_ai' },
  { path: 'reports/farmers',   Component: FarmersReport,        permKey: 'reports_farmers' },
  { path: 'reports/fodder',    Component: FodderReport,         permKey: 'reports_fodder' },
  { path: 'reports/district-common-abstract', Component: DistrictAbstractReport, permKey: 'reports_district_common_abstract' },
  { path: 'reports/drug-indent/district-wise-abstract', Component: DistrictWiseAbstractReport, permKey: 'reports_drug_indent_district_wise_abstract' },
  { path: 'reports/drug-indent/district-wise-institution-count', Component: DistrictWiseInstitutionCountReport, permKey: 'reports_drug_indent_district_wise_institution_count' },
  { path: 'reports/drug-indent/institution-wise', Component: InstitutionWiseDrugReport, permKey: 'reports_drug_indent_institution_wise' },
  { path: 'reports/drug-indent/institution-progress', Component: InstitutionProgressReport, permKey: 'reports_drug_indent_institution_progress' },
  { path: 'reports/drug-indent/district-wise-detailed', Component: DistrictWiseDetailedDrugReport, permKey: 'reports_drug_indent_district_wise_detailed' },
  { path: 'reports/drug-indent/district-wise', Component: DistrictWiseDrugReport, permKey: 'reports_drug_indent_district_wise' },
  { path: 'reports/drug-indent/group-wise', Component: GroupWiseDistrictIndentReport, permKey: 'reports_drug_indent_group_wise' },

  { path: 'reports/drug-indent-26-27/district-wise-abstract', Component: DistrictWiseAbstractReport2627, permKey: 'reports_drug_indent_26_27_district_wise_abstract' },
  { path: 'reports/drug-indent-26-27/district-wise-institution-count', Component: DistrictWiseInstitutionCountReport2627, permKey: 'reports_drug_indent_26_27_district_wise_institution_count' },
  { path: 'reports/drug-indent-26-27/institution-wise', Component: InstitutionWiseDrugReport2627, permKey: 'reports_drug_indent_26_27_institution_wise' },
  { path: 'reports/drug-indent-26-27/institution-progress', Component: InstitutionProgressReport2627, permKey: 'reports_drug_indent_26_27_institution_progress' },
  { path: 'reports/drug-indent-26-27/district-wise-detailed', Component: DistrictWiseDetailedDrugReport2627, permKey: 'reports_drug_indent_26_27_district_wise_detailed' },
  { path: 'reports/drug-indent-26-27/district-wise', Component: DistrictWiseDrugReport2627, permKey: 'reports_drug_indent_26_27_district_wise' },
  { path: 'reports/drug-indent-26-27/group-wise', Component: GroupWiseDistrictIndentReport2627, permKey: 'reports_drug_indent_26_27_group_wise' },
  { path: 'reports/inventory', Component: ReportDetailPage,     permKey: 'reports_inventory' },
  { path: 'reports/drug-wise', Component: ReportDetailPage,     permKey: 'reports_drug_wise' },
  { path: 'settings/*',        Component: SettingsPage,         permKey: 'settings'             },
];

const citizenRoutes = [
  { path: 'citizen/registration',     Component: CitizenRegistrationPage, permKey: 'citizens_registration' },
  { path: 'citizen/fodder-seed',      Component: CitizenFodderSeedPage,   permKey: 'citizens_fodder_seed'  },
  { path: 'citizen/sex-sorted-semen', Component: CitizenSexSortedSemenPage, permKey: 'citizens_sss'        },
  { path: 'citizen/deworming',        Component: CitizenDewormingPage,    permKey: 'citizens_deworming'    },
  { path: 'citizen/ai',               Component: CitizenAIPage,           permKey: 'citizens_ai'           },
  { path: 'citizen/grievances',       Component: CitizenGrievancesPage,   permKey: 'citizens_grievances'   },
];

// ─── Permission Denied Page ────────────────────────────────────────────────────
function PermissionDenied() {
  const navigate = useNavigate();
  return (
    <div style={{
      minHeight: '60vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      fontFamily: 'Inter, sans-serif', textAlign: 'center', padding: '40px 24px',
    }}>
      <div style={{
        width: 72, height: 72, borderRadius: '50%',
        background: 'linear-gradient(135deg,#fef2f2,#fee2e2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 32, marginBottom: 20,
      }}>🔒</div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
        Access Restricted
      </h2>
      <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 24, maxWidth: 360 }}>
        You do not have permission to view this page. Please contact your administrator
        to request access.
      </p>
      <button
        onClick={() => navigate('/dashboard')}
        style={{
          padding: '10px 24px', background: '#1a56db', color: '#fff',
          border: 'none', borderRadius: 8, cursor: 'pointer',
          fontSize: 14, fontWeight: 600, fontFamily: 'Inter, sans-serif',
        }}
      >
        ← Back to Dashboard
      </button>
    </div>
  );
}

// ─── Route guard: requires auth + optional permKey ─────────────────────────────
function ProtectedRoute({ children, permKey }) {
  const { isAuthenticated, loading, hasPermission, refreshPermissions } = useAuth();

  React.useEffect(() => {
    if (isAuthenticated && refreshPermissions) {
      refreshPermissions().catch(() => {});
    }
  }, [isAuthenticated, refreshPermissions, permKey]);

  if (loading) return <LoadingSpinner fullPage />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // If a permKey is specified, check view permission
  if (permKey && !hasPermission(permKey, 'view')) {
    return <PermissionDenied />;
  }

  return children;
}

function AppRoutes() {
  const { isAuthenticated, hasPermission } = useAuth();

  // Redirect to dashboard if accessible, otherwise to sync
  const defaultRedirect = hasPermission('dashboard', 'view') ? '/dashboard' : '/sync';

  return (
    <Suspense fallback={<LoadingSpinner fullPage />}>
      <Routes>
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to={defaultRedirect} replace /> : <LoginPage />}
        />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <SyncProvider>
                <AppShell />
              </SyncProvider>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to={defaultRedirect} replace />} />
          {protectedRoutes.map(({ path, Component, permKey }) => (
            <Route
              key={path}
              path={path}
              element={
                <ProtectedRoute permKey={permKey}>
                  <Component />
                </ProtectedRoute>
              }
            />
          ))}
          {citizenRoutes.map(({ path, Component, permKey }) => (
            <Route
              key={path}
              path={path}
              element={
                <ProtectedRoute permKey={permKey}>
                  <Component />
                </ProtectedRoute>
              }
            />
          ))}
          <Route path="*" element={<Navigate to={defaultRedirect} replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ErrorBoundary>
            <AppRoutes />
            <Toaster
              position="bottom-left"
              toastOptions={{
                duration: 3800,
                style: { fontSize: 13, fontFamily: 'Inter, sans-serif' },
                success: { iconTheme: { primary: '#059669', secondary: '#fff' } },
                error: { iconTheme: { primary: '#dc2626', secondary: '#fff' } },
              }}
            />
          </ErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
